/**
 * Cobertura y precio del método de envío «Mensajero Perrón Store» (ps_delivery) en el front.
 *
 * Espejo EXACTO del backend (app/Support/DeliveryCoverage.php): el servidor es la fuente de verdad y
 * valida cobertura/precio (422 fuera del GAM) y RE-DERIVA/SOBRESCRIBE el `delivery_price` al crear el
 * pedido/sesión; esta copia alimenta solo la UX —deshabilitar la opción, disparar el aviso de cambio de
 * método y mostrar/estimar el precio— en checkout y en el modal de precompra.
 *
 * Un test de contrato en cada repo fija las 18 entradas verbatim: si divergen, el test cae.
 *
 * Matching por PAR (provincia, cantón): los nombres de cantón pueden repetirse entre provincias y el
 * cantón central comparte nombre con su provincia (p. ej. «San José»), así que la provincia desambigua.
 * Los nombres coinciden verbatim con el catálogo canónico (public/assets/locations/cr.json), tildes incl.
 */

/**
 * Precio del mensajero POR PROVINCIA en colones enteros (IVA incluido). La cobertura se mantiene por
 * cantón (ver `PS_DELIVERY_COVERAGE`) pero la tarifa depende de la provincia del cantón cubierto.
 *
 * ⚠️ Espejo EXACTO del backend (`app/Support/DeliveryCoverage.php`): como el servidor re-deriva y
 * SOBRESCRIBE el precio, si esta tabla diverge de la del backend el cliente vería un importe y se le
 * cobraría otro. Mantener sincronizadas ambas (mismo criterio que la whitelist de cobertura).
 */
export const PS_DELIVERY_PRICE_BY_PROVINCE: Readonly<Record<string, number>> = {
  'San José': 4500,
  Heredia: 7000,
  Alajuela: 8000,
  Cartago: 7000,
};

/** Tarifa mínima entre provincias cubiertas — se usa como «Desde ₡X» y como estimación de reserva. */
export const PS_DELIVERY_MIN_PRICE = 4500;

/**
 * Precio del mensajero para la provincia dada. Para una provincia cubierta devuelve su tarifa; para
 * una provincia desconocida/nula devuelve el mínimo (estimación transitoria: el servidor valida
 * cobertura y re-deriva el precio real al confirmar).
 */
export function psDeliveryPriceFor(province: string | null | undefined): number {
  return (province && PS_DELIVERY_PRICE_BY_PROVINCE[province]) || PS_DELIVERY_MIN_PRICE;
}

/** True si la provincia tiene una tarifa propia (cubierta): permite mostrar el precio EXACTO, no «Desde». */
export function isPsDeliveryProvince(province: string | null | undefined): boolean {
  return !!province && province in PS_DELIVERY_PRICE_BY_PROVINCE;
}

/** Whitelist canónica (18 cantones del núcleo GAM), por provincia. */
export const PS_DELIVERY_COVERAGE: Readonly<Record<string, readonly string[]>> = {
  'San José': [
    'San José', 'Desamparados', 'Mora', 'Goicoechea', 'Santa Ana',
    'Alajuelita', 'Tibás', 'Moravia', 'Montes de Oca', 'Curridabat', 'León Cortés Castro',
  ],
  Alajuela: ['Alajuela'],
  Cartago: ['Cartago', 'La Unión'],
  Heredia: ['Heredia', 'Barva', 'Santo Domingo', 'San Pablo'],
};

/** True si el par (provincia, cantón) está dentro de la cobertura del mensajero. */
export function isPsDeliveryCovered(
  province: string | null | undefined,
  canton: string | null | undefined,
): boolean {
  if (!province || !canton) return false;
  return (PS_DELIVERY_COVERAGE[province] ?? []).includes(canton);
}
